import { localKey } from '@/components/molecules/Table';
import { filterStoreKey } from '@/components/molecules/Table/filter';
import { fetchRequestWithToken } from '@/config/axios';
import { SOCKET_EVENT_NAMES } from '@/constants/socket.events';
import { notify } from '@/helpers/index';
import routeNames from '@/routes/routeNames';
import { AuthStore } from '@/state/AuthenticationStore';
import { workspaceStore } from '@/state/WorkSpaceStore';
import { SocketRequest } from '@/config/socket';
import { Conversation, CONVERSATION_CHANNEL } from '@/types/conversation.types';
import {
  ActiveWorkspace,
  UserWorkspaceInfo,
  Workspace,
} from '@/types/workspace.type';
import { replaceRouteParam } from '@/utils/routes';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

type WorkspacePayload = {
  company_size: string | null;
  workspace_name: string | null;
  timezone: string | null;
};

export const useWorkspace = () => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const switchWorkspace = async (workspace: Workspace) => {
    AuthStore.set_switching_workspace(true);
    try {
      const response = await SocketRequest<
        { workspace: Workspace },
        UserWorkspaceInfo
      >(SOCKET_EVENT_NAMES.SWITCH_WORKSPACE, { data: { workspace } });
      if (response.data) {
        AuthStore.set_user_workspace_info(response.data);
        localStorage.removeItem(localKey);
        localStorage.removeItem(filterStoreKey);
        navigate(
          replaceRouteParam(
            `${routeNames.dashboard.home}`,
            'workspaceId',
            workspace._id,
          ),
          { replace: true },
        );
        window.location.reload();
      }
    } catch (error) {
      notify('error', 'Failed to switch workspace');
    } finally {
      AuthStore.set_switching_workspace(false);
    }
  };

  const createWorkspace = async (data: WorkspacePayload) => {
    setIsLoading(true);
    try {
      const response = await SocketRequest<WorkspacePayload, ActiveWorkspace>(
        SOCKET_EVENT_NAMES.CREATE_WORKSPACE,
        { data },
      );
      if (response.data) {
        (window.location as any) = replaceRouteParam(
          `${routeNames.dashboard.home}`,
          'workspaceId',
          response.data.workspace._id,
        );
      }
    } catch (error) {
      notify('error', 'Failed to create workspace');
    } finally {
      setIsLoading(false);
    }
  };

  const updateWorkspace = async (data: Partial<WorkspacePayload>) => {
    setIsLoading(true);
    try {
      const response = await SocketRequest<
        Partial<WorkspacePayload>,
        Workspace
      >(SOCKET_EVENT_NAMES.UPDATE_WORKSPACE, { data });
      if (response.data) {
        notify('success', 'Workspace updated successfully');
        AuthStore.update_workspace_in_active_workspace(response.data);
      } else if (response.error) {
        notify('error', response.error as string);
      }
    } catch (error) {
      notify('error', 'Failed to update workspace');
    } finally {
      setIsLoading(false);
    }
  };

  const getWorkspaceMessages = async (channel: CONVERSATION_CHANNEL) => {
    setIsLoading(true);
    try {
      const response = await SocketRequest<
        { channel: CONVERSATION_CHANNEL },
        Conversation[]
      >(SOCKET_EVENT_NAMES.GET_WORKSPACE_MESSAGES, { data: { channel } });
      if (response.data) {
        AuthStore.setWorkspaceMessages(response.data);
      } else if (response.error) {
        notify('error', response.error as string);
      }
    } catch (error) {
      notify('error', 'Failed to fetch workspace messages');
    } finally {
      setIsLoading(false);
    }
  };

  const deleteWorkspace = async (_id: any) => {
    workspaceStore.setLoading(true);
    await fetchRequestWithToken({
      url: `/workspaces/delete-workspace/${_id}`,
      method: 'DELETE',
    });
    workspaceStore.setLoading(false);
  };
  return {
    isLoading,
    switchWorkspace,
    createWorkspace,
    updateWorkspace,
    getWorkspaceMessages,
    deleteWorkspace,
  } as const;
};
